import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
// types
import { M2CCardComms, M2CGratificationCardComms } from "../m2c-halfcard-wrapper";
import { HalfcardVariants } from "@super-ui/components/m2c-swiggy-one-halfcard/types";
import { GlobalOverlayPriority, HalfcardIds, OverlayType, PlanDetails } from "@swiggy-private/cart-types";
// constant
import { FlowType } from "@super-ui/interfaces";
import { M2C_HALFCARD_FREQUENCY_CAPPING_KEY } from "../constants";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
// helpers
import {
    CartOverlaysContext,
    DEFAULT_FREQUENCY_CAP_VALUE,
    ProceedMode,
    SessionVisibility,
    useLocalStorageBasedFrequencyCapping,
} from "@swiggy-private/checkout-core";
import { LocalStorage } from "@swiggy-private/core-ui";
import { SWIGGY_ONE_COMPONENT, SWIGGY_ONE_STATE, recordSwiggyOneNREvent } from "@super-ui/helpers/SwiggyOneNREvents";
import { areValidCtas } from "../helpers";
// context
import { M2CHalfcardContext } from "@super-ui/contexts";

interface UseGetM2CHalfcardProps {
    m2cCardComms?: M2CCardComms;
    planDetails: PlanDetails;
    addSuper: (flowType: FlowType) => void;
    hideM2CHalfcard?: boolean;
    m2cGratificationCardComms?: M2CGratificationCardComms;
    updateCartInProgress?: boolean;
    setShowM2Chalfcard?: React.Dispatch<React.SetStateAction<boolean>>;
    ignoreCapping?: boolean;
    flowType?: FlowType;
}

export function useGetM2CHalfcardProps(m2cHalfcardProps: UseGetM2CHalfcardProps) {
    const [shouldMountM2C, setShouldMountM2C] = useState(false);
    const {
        m2cCardComms,
        m2cGratificationCardComms,
        addSuper,
        planDetails,
        updateCartInProgress,
        setShowM2Chalfcard = () => void 0,
        ignoreCapping,
        flowType,
    } = m2cHalfcardProps;
    const { sessionId, gratificationCardAutoCloseDelay } = useContext(M2CHalfcardContext);
    const superAddCtaClickedRef = useRef(false);
    const { registerOverlays, enabledOverlayId } = useContext(CartOverlaysContext);
    const registerM2CHalfcardRef = useRef<boolean>(false);

    const {
        logo_ctx: logoCtx,
        title,
        subtitles,
        ctas,
        note,
        content,
        content_image: contentImage,
        attribute_values: attributeValues,
        variant: designVariantId,
        frequency_cap: frequencyCap,
        identifiers,
        asset_ctx: assetCtx,
    } = m2cCardComms || ({} as M2CCardComms);

    const {
        analyticsEvents: {
            onClickM2CHalfcardAddCta,
            onImpressionM2CHalfcard,
            onImpressionM2CHalfcardAddCta,
            onImpressionM2CHalfcardSkipCta,
        },
        frequencyCappingKey = M2C_HALFCARD_FREQUENCY_CAPPING_KEY,
    } = useContext(M2CHalfcardContext);

    const frequencyCapPayload = {
        id: frequencyCap?.id,
        key: frequencyCappingKey,
        maxFrequency: Number(frequencyCap?.count),
        sessionVisibility: SessionVisibility.ONCE_PER_SESSION,
        sessionId,
    };

    const { shouldProceed } = useLocalStorageBasedFrequencyCapping(frequencyCapPayload);

    const isDataValidated = useCallback(() => {
        if (isEmpty(m2cCardComms) || isEmpty(planDetails) || !areValidCtas(ctas)) {
            return false;
        }
        return true;
    }, [ctas, m2cCardComms, planDetails]);

    useEffect(() => {
        if (
            isDataValidated() &&
            (ignoreCapping || (shouldProceed({ mode: ProceedMode.ONLY_CHECK }) && !registerM2CHalfcardRef.current))
        ) {
            registerOverlays({
                overlayType: OverlayType.HALFCARD,
                id: HalfcardIds.M2C_HALFCARD,
                priority: GlobalOverlayPriority.M2C,
            });
            registerM2CHalfcardRef.current = true;
        }
    }, [enabledOverlayId, isDataValidated, registerOverlays, shouldProceed, ignoreCapping]);

    useEffect(() => {
        if (enabledOverlayId === HalfcardIds.M2C_HALFCARD && isDataValidated() && (ignoreCapping || shouldProceed())) {
            setShouldMountM2C(true);
            setShowM2Chalfcard(true);
        }
    }, [enabledOverlayId, shouldProceed, setShowM2Chalfcard, setShouldMountM2C, isDataValidated, ignoreCapping]);

    useEffect(() => {
        if (!isDataValidated() || ignoreCapping) return;
        if (!shouldProceed({ mode: ProceedMode.ONLY_CHECK })) {
            const stored = LocalStorage.getItem(frequencyCappingKey, DEFAULT_FREQUENCY_CAP_VALUE);
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.M2C_HALFCARD, SWIGGY_ONE_STATE.HIDDEN_FREQUENCY_CAPPED, {
                frequencyCapId: frequencyCap?.id || "",
                maxFrequency: String(frequencyCap?.count ?? ""),
                storedCount: String(stored?.counterValue ?? 0),
                currentSessionId: sessionId || "",
                storedSessionId: stored?.sessionId || "",
            });
        }
    }, [frequencyCap, frequencyCappingKey, ignoreCapping, isDataValidated, sessionId, shouldProceed]);

    // Hide M2C halfcard if super add cta is clicked and cart is updated but gratification card data is null
    useEffect(() => {
        if (superAddCtaClickedRef.current && !updateCartInProgress && isEmpty(m2cGratificationCardComms)) {
            setShouldMountM2C(false);
            setShowM2Chalfcard(false);
        }
    }, [m2cGratificationCardComms, superAddCtaClickedRef, updateCartInProgress, setShowM2Chalfcard]);

    const addSuperFromTGBottomsheet = useCallback(
        (ctaText: string) => {
            superAddCtaClickedRef.current = true;
            onClickM2CHalfcardAddCta(ctaText);
            addSuper(flowType || FlowType.M2C);
        },
        [addSuper, onClickM2CHalfcardAddCta, flowType],
    );

    const closeBottomSheet = useCallback(() => {
        setShouldMountM2C(false);
    }, []);

    const bottomSheetData = useMemo(
        () => ({
            logoCtx,
            title,
            subtitles,
            identifiers,
            assetCtx,
            ctas,
            note,
            content,
            contentImage,
            attributeValues,
            designVariantId: (designVariantId?.toUpperCase() || HalfcardVariants.DEFAULT) as HalfcardVariants,
        }),
        [
            logoCtx,
            title,
            subtitles,
            identifiers,
            assetCtx,
            ctas,
            note,
            content,
            contentImage,
            attributeValues,
            designVariantId,
        ],
    );

    const preStateData = useMemo(() => {
        if (!isEmpty(m2cCardComms)) {
            return {
                bottomSheetData,
                addSuperFromTGBottomsheet,
                closeBottomSheet,
                analyticsEvents: {
                    onImpressionM2CHalfcard,
                    onImpressionM2CHalfcardAddCta,
                    onImpressionM2CHalfcardSkipCta,
                },
            };
        }
    }, [
        addSuperFromTGBottomsheet,
        bottomSheetData,
        closeBottomSheet,
        m2cCardComms,
        onImpressionM2CHalfcard,
        onImpressionM2CHalfcardAddCta,
        onImpressionM2CHalfcardSkipCta,
    ]);

    const postStateData = useMemo(() => {
        if (!isEmpty(m2cGratificationCardComms)) {
            const { header: headerText, logo_ctx: logoCtx, title, content } = m2cGratificationCardComms;
            return {
                headerText,
                logoCtx,
                title,
                content,
                duration: gratificationCardAutoCloseDelay,
            };
        }
    }, [gratificationCardAutoCloseDelay, m2cGratificationCardComms]);

    return {
        shouldMountM2C,
        preStateData,
        postStateData,
    };
}
